import { Injectable } from '@angular/core';
import { Http, Headers, RequestOptions, ResponseContentType } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';

import {
  SkillSpecializationCreateRequest,
  SkillSpecializationDeactivateParams,
  SkillSpecializationCreationPermissions,
  SkillSpecializationResponse,
  SkillSpecializationListItem,
  CuratorAssignRequest,
  TheoryLevelRequest,
  TheoryTestGenerationRequest,
  PracticeLevelRequest,
  SkillPromptTemplateResponse,
  SkillTestQuestion,
  SkillDocumentPreviewLinkResponse,
  SkillLevelStructureUpdateRequest,
  SkillTrainingRequirementDeleteParams,
  SkillTrainingRequirementScope,
  SkillTrainingRequirementUpdateRequest,
  SkillUploadDocumentCommand,
} from '../models/skill-learning.model';
import {
  SKILL_DOCUMENT_PREVIEWS_API,
  SKILL_DOCUMENT_FORM_FIELD,
  SKILL_SPECIALIZATIONS_API,
} from '../constants/skill-api.constants';

@Injectable()
export class SkillSpecializationApiService {

  private readonly jsonHeaders = new Headers({ 'Content-Type': 'application/json' });
  private readonly jsonOptions = new RequestOptions({ headers: this.jsonHeaders });

  constructor(private http: Http) {}

  fetchAllSpecializations(departmentId?: string): Observable<SkillSpecializationListItem[]> {
    return this.http
      .get(SKILL_SPECIALIZATIONS_API.ADMIN_LIST(departmentId))
      .map(response => response.json() as SkillSpecializationListItem[]);
  }

  fetchAvailableSpecializations(departmentId?: string): Observable<SkillSpecializationListItem[]> {
    return this.http
      .get(SKILL_SPECIALIZATIONS_API.AVAILABLE(departmentId))
      .map(response => response.json() as SkillSpecializationListItem[]);
  }

  fetchSpecializationById(id: string, departmentId?: string): Observable<SkillSpecializationResponse> {
    const url = departmentId
      ? SKILL_SPECIALIZATIONS_API.BY_ID_FOR_DEPARTMENT(id, departmentId)
      : SKILL_SPECIALIZATIONS_API.BY_ID(id);
    return this.http
      .get(url)
      .map(response => response.json() as SkillSpecializationResponse);
  }

  fetchTheoryDirectionsPrompt(id: string, level: number): Observable<SkillPromptTemplateResponse> {
    return this.http
      .get(SKILL_SPECIALIZATIONS_API.THEORY_DIRECTIONS_PROMPT(id, level))
      .map(response => response.json() as SkillPromptTemplateResponse);
  }

  fetchSpecializationsByGroup(groupId: string): Observable<SkillSpecializationListItem[]> {
    return this.http
      .get(SKILL_SPECIALIZATIONS_API.BY_GROUP(groupId))
      .map(response => response.json() as SkillSpecializationListItem[]);
  }

  fetchMyCuratedSpecializations(): Observable<SkillSpecializationListItem[]> {
    return this.http
      .get(SKILL_SPECIALIZATIONS_API.MY_CURATED)
      .map(response => response.json() as SkillSpecializationListItem[]);
  }

  createSpecialization(request: SkillSpecializationCreateRequest): Observable<SkillSpecializationResponse> {
    return this.http
      .post(SKILL_SPECIALIZATIONS_API.BASE, JSON.stringify(request), this.jsonOptions)
      .map(response => response.json() as SkillSpecializationResponse);
  }

  updateSpecialization(id: string, request: SkillSpecializationCreateRequest): Observable<SkillSpecializationResponse> {
    return this.http
      .put(SKILL_SPECIALIZATIONS_API.BY_ID(id), JSON.stringify(request), this.jsonOptions)
      .map(response => response.json() as SkillSpecializationResponse);
  }

  updateProbationRequirement(id: string, required: boolean): Observable<SkillSpecializationResponse> {
    return this.http
      .put(
        SKILL_SPECIALIZATIONS_API.PROBATION_REQUIREMENT(id),
        JSON.stringify({ required: required }),
        this.jsonOptions,
      )
      .map(response => response.json() as SkillSpecializationResponse);
  }

  updateStructure(
    id: string,
    request: SkillLevelStructureUpdateRequest,
  ): Observable<SkillSpecializationResponse> {
    return this.http.put(
      SKILL_SPECIALIZATIONS_API.STRUCTURE(id),
      JSON.stringify(request),
      this.jsonOptions,
    ).map(response => response.json() as SkillSpecializationResponse);
  }

  updateRequirement(
    id: string,
    scope: SkillTrainingRequirementScope,
    request: SkillTrainingRequirementUpdateRequest,
  ): Observable<SkillSpecializationResponse> {
    return this.http.put(
      SKILL_SPECIALIZATIONS_API.REQUIREMENT(id, scope),
      JSON.stringify(request),
      this.jsonOptions,
    ).map(response => response.json() as SkillSpecializationResponse);
  }

  deleteRequirement(
    id: string,
    scope: SkillTrainingRequirementScope,
    params: SkillTrainingRequirementDeleteParams,
  ): Observable<SkillSpecializationResponse> {
    return this.http.delete(SKILL_SPECIALIZATIONS_API.REQUIREMENT_DELETE(id, scope, params))
      .map(response => response.json() as SkillSpecializationResponse);
  }

  fetchCreationPermissions(): Observable<SkillSpecializationCreationPermissions> {
    return this.http
      .get(SKILL_SPECIALIZATIONS_API.CREATION_PERMISSIONS)
      .map(response => response.json() as SkillSpecializationCreationPermissions);
  }

  createFillTask(id: string): Observable<SkillSpecializationResponse> {
    return this.http
      .post(SKILL_SPECIALIZATIONS_API.FILL_TASK(id), null, this.jsonOptions)
      .map(response => response.json() as SkillSpecializationResponse);
  }

  deactivateSpecialization(id: string, params: SkillSpecializationDeactivateParams): Observable<void> {
    return this.http
      .delete(SKILL_SPECIALIZATIONS_API.DEACTIVATE(id, params))
      .map(() => undefined);
  }

  assignCurators(id: string, request: CuratorAssignRequest): Observable<void> {
    return this.http
      .post(SKILL_SPECIALIZATIONS_API.CURATORS(id), JSON.stringify(request), this.jsonOptions)
      .map(() => undefined);
  }

  removeCurator(id: string, curatorId: string, departmentId?: string): Observable<void> {
    return this.http
      .delete(SKILL_SPECIALIZATIONS_API.CURATOR_BY_ID(id, curatorId, departmentId))
      .map(() => undefined);
  }

  updateTheoryLevel(
    id: string,
    level: number,
    request: TheoryLevelRequest,
  ): Observable<SkillSpecializationResponse> {
    return this.http
      .put(SKILL_SPECIALIZATIONS_API.THEORY_LEVEL(id, level), JSON.stringify(request), this.jsonOptions)
      .map(response => response.json() as SkillSpecializationResponse);
  }

  generateTheoryTestQuestions(
    id: string,
    level: number,
    request: TheoryTestGenerationRequest,
  ): Observable<SkillTestQuestion[]> {
    return this.http
      .post(
        SKILL_SPECIALIZATIONS_API.THEORY_TESTS_GENERATE(id, level),
        JSON.stringify(request),
        this.jsonOptions,
      )
      .map(response => response.json() as SkillTestQuestion[]);
  }

  updatePracticeLevel(
    id: string,
    level: number,
    request: PracticeLevelRequest,
  ): Observable<SkillSpecializationResponse> {
    return this.http
      .put(SKILL_SPECIALIZATIONS_API.PRACTICE_LEVEL(id, level), JSON.stringify(request), this.jsonOptions)
      .map(response => response.json() as SkillSpecializationResponse);
  }

  uploadLevelDocument(id: string, command: SkillUploadDocumentCommand): Observable<string> {
    const formData = new FormData();
    formData.append(SKILL_DOCUMENT_FORM_FIELD, command.file);
    const url = SKILL_SPECIALIZATIONS_API.LEVEL_DOCUMENT_UPLOAD(
      id,
      command.level,
      command.expectedRevision,
    );
    return this.http
      .post(url, formData)
      .map(response => response.text());
  }

  buildLevelDocumentDownloadUrl(id: string, level: number): string {
    return SKILL_SPECIALIZATIONS_API.LEVEL_DOCUMENT(id, level);
  }

  downloadLevelDocumentBlob(id: string, level: number): Observable<Blob> {
    const options = new RequestOptions({ responseType: ResponseContentType.Blob });
    return this.http
      .get(SKILL_SPECIALIZATIONS_API.LEVEL_DOCUMENT(id, level), options)
      .map(response => response.blob());
  }

  createLevelDocumentPreviewLink(
    id: string,
    level: number,
  ): Observable<SkillDocumentPreviewLinkResponse> {
    return this.http
      .post(SKILL_SPECIALIZATIONS_API.LEVEL_DOCUMENT_PREVIEW_LINK(id, level), null, this.jsonOptions)
      .map(response => response.json() as SkillDocumentPreviewLinkResponse);
  }

  buildDocumentPreviewUrl(token: string, fileName: string): string {
    return SKILL_DOCUMENT_PREVIEWS_API.BY_TOKEN(token, fileName);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/services/skill-specialization-api.service.ts